import React, { useEffect, useState } from 'react';
import { Button, Snackbar, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MAIN_BUNDLE_RE = /\/static\/js\/main\.([a-f0-9]+)\.js/;

// The bundle hash this tab is running, read from the page's own script tag.
// Null in development, where CRA serves an unhashed bundle.
const getRunningBundleHash = () => {
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  for (const script of scripts) {
    const match = script.getAttribute('src').match(MAIN_BUNDLE_RE);
    if (match) return match[1];
  }
  return null;
};

/**
 * Tabs left open across a deploy keep running the old code, so fixes look
 * like they never shipped (TKT-427/431/441/463/464 retests, Oct 21 2026).
 * Every few minutes, and whenever the tab regains focus, compare the bundle
 * in the server's index.html with the one this tab loaded, and offer a reload
 * when they differ.
 */
const UpdateAvailablePrompt = () => {
  const { t } = useTranslation();
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const runningHash = getRunningBundleHash();
    if (!runningHash) return undefined;

    let cancelled = false;
    const check = async () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      try {
        const res = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const match = (await res.text()).match(MAIN_BUNDLE_RE);
        if (!cancelled && match && match[1] !== runningHash) {
          setUpdateAvailable(true);
        }
      } catch (err) {
        // Offline or a deploy in progress: try again on the next tick.
      }
    };

    const interval = setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, []);

  return (
    <Snackbar
      open={updateAvailable}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      data-demo-target="update-available-prompt"
    >
      <Alert
        severity="info"
        variant="filled"
        action={(
          <Button color="inherit" size="small" onClick={() => window.location.reload()}>
            {t('common.reloadNow')}
          </Button>
        )}
      >
        {t('common.newVersionAvailable')}
      </Alert>
    </Snackbar>
  );
};

export default UpdateAvailablePrompt;
